import { useEffect, useRef, useState, type ReactNode } from 'react';
import { withinHours, type Point } from '@rydres/shared';
import { AddressInput, Field, Input, MapPreview, PlaceButtons, driveText, useAddressTarget, useMyLocation, useRoute, type PickedAddress } from '@rydres/ui';
import { api, geoApi } from '../lib/api.js';
import type { BusyRange, PublicPage } from '../lib/types.js';

/** Local wall-clock input → ISO with the browser's offset. Riders book in their own time zone. */
export const toIso = (local: string) => (local ? new Date(local).toISOString() : '');
const minLocal = (hoursAhead: number) => {
  const d = new Date(Date.now() + hoursAhead * 3_600_000);
  d.setSeconds(0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export interface TripForm {
  startsAt: string;
  passengers: number;
  pickupAddress: string;
  dropoffAddress: string;
  pickupPoint: Point | null;
  dropoffPoint: Point | null;
}

export const EMPTY_TRIP: TripForm = { startsAt: '', passengers: 1, pickupAddress: '', dropoffAddress: '', pickupPoint: null, dropoffPoint: null };

type Service = PublicPage['services'][number];

/** What is wrong with a pickup time, from the driver's hours and rounded busy times; null when it looks free. */
export function useSlotWarning(page: PublicPage, startsAt: string, durationMin: number) {
  const [busy, setBusy] = useState<BusyRange[]>([]);
  useEffect(() => {
    const from = new Date().toISOString();
    const to = new Date(Date.now() + page.maxAdvanceDays * 86_400_000).toISOString();
    api
      .call<{ busy: BusyRange[] }>('GET', `/public/pages/${encodeURIComponent(page.handle)}/availability`, { auth: false, query: { from, to } })
      .then((r) => setBusy(r.busy), () => undefined);
  }, [page.handle, page.maxAdvanceDays]);
  if (!startsAt) return null;
  const start = new Date(startsAt);
  if (Number.isNaN(start.getTime())) return null;
  if (!withinHours(page.hours, start, page.timezone)) return 'The driver does not take pickups at that time.';
  const end = new Date(start.getTime() + (durationMin + 30) * 60_000);
  const hit = busy.find((b) => new Date(b.start) < end && new Date(b.end) > start);
  return hit ? 'The driver is already booked around that time. Try another time.' : null;
}

/**
 * The trip part of a booking or a ride request: when, how many, from where to where, with the place
 * buttons filling whichever address had focus last. Choosing a `service` fills in its own addresses,
 * unless the rider already typed something else.
 */
export function TripFields({
  page,
  form,
  onChange,
  service,
  places,
  warning,
  warningExtra,
  placesFooter,
}: {
  page: PublicPage;
  form: TripForm;
  onChange: (update: (f: TripForm) => TripForm) => void;
  service?: Service;
  places: ReadonlyArray<{ label: string; address: string; point?: Point | null }>;
  warning: string | null;
  warningExtra?: ReactNode;
  placesFooter?: ReactNode;
}) {
  const setPlace = (field: 'pickup' | 'dropoff', p: PickedAddress) => onChange((f) => ({ ...f, [`${field}Address`]: p.address, [`${field}Point`]: p.point }));
  const address = useAddressTarget();
  const here = useMyLocation(geoApi);
  const route = useRoute(geoApi, form.pickupPoint, form.dropoffPoint);
  const lastService = useRef<Service | undefined>(undefined);
  useEffect(() => {
    const prev = lastService.current;
    lastService.current = service;
    onChange((f) => {
      const pickup = !f.pickupAddress || f.pickupAddress === prev?.pickupAddress;
      const dropoff = !f.dropoffAddress || f.dropoffAddress === prev?.dropoffAddress;
      return {
        ...f,
        pickupAddress: pickup ? (service?.pickupAddress ?? '') : f.pickupAddress,
        pickupPoint: pickup ? (service?.pickupPoint ?? null) : f.pickupPoint,
        dropoffAddress: dropoff ? (service?.dropoffAddress ?? '') : f.dropoffAddress,
        dropoffPoint: dropoff ? (service?.dropoffPoint ?? null) : f.dropoffPoint,
      };
    });
    address.setTarget(service?.pickupAddress && !service.dropoffAddress ? 'dropoff' : 'pickup');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service?.id]);
  const pick = (value: string, point: Point | null) => {
    setPlace(address.target, { address: value, point });
    if (address.target === 'pickup' && !form.dropoffAddress) address.setTarget('dropoff');
  };
  return (
    <>
      <Field label="Pickup date and time" error={warning} hint={page.minNoticeHours ? `At least ${page.minNoticeHours} hours ahead` : undefined}>
        <Input type="datetime-local" required min={minLocal(page.minNoticeHours)} value={form.startsAt} onChange={(e) => onChange((f) => ({ ...f, startsAt: e.target.value }))} />
      </Field>
      {warning && warningExtra}
      <Field label="Passengers">
        <Input type="number" min={1} max={service?.maxPassengers ?? page.vehicle.seats ?? 14} value={form.passengers} onChange={(e) => onChange((f) => ({ ...f, passengers: Number(e.target.value) }))} />
      </Field>
      <div className="space-y-2">
        <Field label="Pickup address">
          <AddressInput required geo={geoApi} near={form.dropoffPoint} placeholder="Terminal, hotel or street address" value={form.pickupAddress} onChange={(p) => setPlace('pickup', p)} onFocus={() => address.setTarget('pickup')} />
        </Field>
        {here.supported && (
          <button type="button" disabled={here.busy} onClick={() => void here.locate().then((p) => p && setPlace('pickup', p))} className="text-sm font-bold text-accent-text underline disabled:opacity-50">
            {here.busy ? 'Finding you…' : 'Use my location'}
          </button>
        )}
        {here.error && <p className="text-xs text-danger">{here.error}</p>}
      </div>
      <Field label="Drop-off address">
        <AddressInput required geo={geoApi} near={form.pickupPoint} placeholder="Where to" value={form.dropoffAddress} onChange={(p) => setPlace('dropoff', p)} onFocus={() => address.setTarget('dropoff')} />
      </Field>
      {(form.pickupPoint || form.dropoffPoint) && (
        <div className="space-y-1 sm:col-span-2">
          <MapPreview from={form.pickupPoint} to={form.dropoffPoint} line={route?.polyline} />
          {route && <p className="text-xs text-muted">{driveText(route)}</p>}
        </div>
      )}
      {(places.length > 0 || placesFooter) && (
        <div className="space-y-2 sm:col-span-2">
          <PlaceButtons places={places} target={address.target} onPick={pick} />
          {placesFooter}
        </div>
      )}
    </>
  );
}
